import {diApiEu} from '@di-shared/api/di';
import type {ApiResponseRecentTripDaysResponse, TripDay, TripDaysResponse} from '@di-shared/api/di/__generated_eu';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {
  endDateOfMonth,
  isDateSameWeek,
  isSameYearAndMonth,
  parseDateWithFormat,
  setDay,
  startDateOfMonth,
  weekOfInterval,
} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

function getFocusTripDate(selectedDate: Date, tripDays: Array<TripDay>) {
  const today = new Date();
  const isCurrentMonth = isSameYearAndMonth(today, selectedDate);
  const existTripDays = tripDays.filter((trip) => trip.tripCnt > 0);
  const firstTripDay = tripDays[0]?.day;

  // 주행 기록이 없는 경우
  // 현재 월: 오늘 날짜에 포커스 / 과거 월: 해당 월의 1일에 포커스
  if (existTripDays.length === 0) {
    if (isCurrentMonth) {
      return today;
    }

    return firstTripDay ? setDay(selectedDate, firstTripDay) : startDateOfMonth(selectedDate);
  }

  // 주행기록이 있는 경우
  // 현재 월: 마지막 주행의 주 및 일자에 포커스 / 과거 월: 최초 주행의 주 및 일자에 포커스
  return setDay(selectedDate, isCurrentMonth ? existTripDays[existTripDays.length - 1].day : existTripDays[0].day);
}

export function isTripOnDay(tripDays: Array<TripDay>, day: number) {
  return tripDays.some((trip) => trip.day === day && trip.tripCnt > 0);
}

export function getWeeksOfMonth(date: Date) {
  const monthStart = startDateOfMonth(date);
  const monthEnd = endDateOfMonth(date);

  return weekOfInterval(monthStart, monthEnd);
}

export function getWeekIndexOfDate(date: Date) {
  return getWeeksOfMonth(date).findIndex((weekStart) => isDateSameWeek(date, weekStart));
}

function isSelectedMonthAfterRecentTripMonth(selectedDate: Date, recentTripDate: Date | null) {
  if (!recentTripDate) {
    return false;
  }

  return startDateOfMonth(selectedDate).getTime() > startDateOfMonth(recentTripDate).getTime();
}

function createEmptyTripDays(selectedMonth: string): TripDaysResponse {
  return {
    month: selectedMonth,
    trips: [],
  };
}

function getMinTripDate(selectedDate: Date, tripDays: Array<TripDay>, minPeriodDate: Date) {
  const firstTripDay = tripDays[0]?.day;

  if (!firstTripDay || firstTripDay === 1) {
    return minPeriodDate;
  }

  return setDay(selectedDate, firstTripDay);
}

export default function useTripDayEu({vin, selectedDate, minPeriodDate}: {vin: string; selectedDate: Date; minPeriodDate: Date}) {
  const selectedMonth = getYYYYMMFormat(selectedDate);

  const {data: recentTripDayData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveRecentTripDays1), vin],
    queryFn: () => diApiEu.drivingHistory.retrieveRecentTripDays1({vin}),
    select: ({data}: ApiResponseRecentTripDaysResponse) => data,
  });
  const verifiedRecentTripDate = parseDateWithFormat(recentTripDayData.recentTripDate);
  const isSelectedRecentTripMonth = verifiedRecentTripDate ? isSameYearAndMonth(verifiedRecentTripDate, selectedDate) : false;
  // recentTripDate가 선택된 월보다 과거 월이면 선택된 월에는 주행이 없으므로
  // /trip/days를 다시 호출하지 않고 빈 trips로 처리한다.
  // 선택된 월이 현재 월인 경우 today까지만 활성화되는 동작은 DatePicker의 maxDate=today가 담당한다.
  // 달력에서 선택 가능 범위는 minDate/maxDate가 결정하고, tripDays는 주행 dot 표시와 focus 계산에만 사용된다.
  const shouldUseEmptyTripDays = isSelectedMonthAfterRecentTripMonth(selectedDate, verifiedRecentTripDate);
  const tripDayQueryKey =
    isSelectedRecentTripMonth || shouldUseEmptyTripDays
      ? getQueryKey(diApiEu.drivingHistory.retrieveRecentTripDays1)
      : getQueryKey(diApiEu.drivingHistory.retrieveTripDays1);

  const {data: tripDayData} = useSuspenseQuery({
    queryKey: [tripDayQueryKey, vin, selectedMonth, shouldUseEmptyTripDays, isSelectedRecentTripMonth],
    queryFn: async (): Promise<TripDaysResponse> => {
      if (shouldUseEmptyTripDays) {
        return createEmptyTripDays(selectedMonth);
      }

      if (isSelectedRecentTripMonth) {
        return recentTripDayData.tripDays ?? createEmptyTripDays(selectedMonth);
      }

      // recentTripDate가 null이면 가입일을 알 수 없으므로 선택된 월의 /trip/days를 조회해
      // 가입일 이후 0건 응답을 포함한 실제 활성 범위를 확인한다.
      const {data} = await diApiEu.drivingHistory.retrieveTripDays1({
        vin,
        month: selectedMonth,
      });

      return data;
    },
  });

  const focusTripDate = getFocusTripDate(selectedDate, tripDayData.trips);
  const minDate = getMinTripDate(selectedDate, tripDayData.trips, minPeriodDate);

  return {
    tripDays: tripDayData.trips,
    minDate,
    focusTripDate,
  };
}
